import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../components/ui/button'
import { CreateOrganizationDialog } from '../components/CreateOrganizationDialog'
import { PageHeader } from '../components/shared/PageHeader'
import { SECTION_ICONS } from '../lib/section-icons'
import { useIsPlatformAdmin } from '../hooks/useAuth'

// Landing page shown when a logged-in user has no org memberships yet. The
// post-login flow in $org.tsx redirects here when `default_org_code` is
// empty. We offer two ways out: checking pending invitations (the email is
// addressed to the same user, so they can accept from their inbox) or
// creating a fresh organisation via the shared CreateOrganizationDialog, which
// only a platform admin may do (POST /organizations is gated server-side).
export const Route = createFileRoute('/no-organization')({
    component: NoOrganizationPage,
})

function NoOrganizationPage() {
    const { t } = useTranslation('multi-org')
    const [createOpen, setCreateOpen] = useState(false)
    const isPlatformAdmin = useIsPlatformAdmin()

    return (
        <div
            className="max-w-xl mx-auto p-8 space-y-6"
            data-testid="no-organization-page"
        >
            <PageHeader
                icon={SECTION_ICONS.organizations}
                title={t('no_organization.title')}
                description={t('no_organization.description')}
            />

            <div className="flex flex-col sm:flex-row gap-3">
                {/* Stub: we link to the invitation inbox route when one exists.
                    For now we point at /invitations which renders a "not
                    found" for plain tokens — acceptable as a placeholder per
                    the Implementation Plan §5. */}
                <Button
                    asChild
                    variant="outline"
                    data-testid="check-invitations-btn"
                >
                    <a href="mailto:" aria-disabled>
                        {t('no_organization.check_invitations')}
                    </a>
                </Button>
                {isPlatformAdmin && (
                    <Button
                        data-testid="create-organization-btn"
                        onClick={() => setCreateOpen(true)}
                    >
                        {t('no_organization.create_org')}
                    </Button>
                )}
            </div>

            <CreateOrganizationDialog
                open={createOpen}
                onOpenChange={setCreateOpen}
                onSuccess={(created) => {
                    setCreateOpen(false)
                    // Hard navigation is fine here — the newly-created org
                    // needs a fresh auth context (the JWT issued during
                    // login is scoped to the previous org, if any). An
                    // organization created from a template starts empty: its
                    // types are brought over by the first sync.
                    if (created.fromTemplate) {
                        window.location.assign(`/${created.code}/types/sync`)
                        return
                    }
                    window.location.reload()
                }}
            />
        </div>
    )
}
